import {
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  FormHelperText,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MuiAccordion from '@mui/material/Accordion';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import Button from 'tailwind-components/Button/Button';
import { STATE_LIST, USER_TYPES } from 'constants/enum';
import icons from 'constants/icons';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAppointmentsViewData,
  getSelectedAgenciesAgents,
  getSpecificAgencyData,
  getStatementsPeriods
} from 'state/actions/actions';
import SimpleFilter from './SimpleFilter/SimpleFilter';

const Accordion = styled(props => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&::before': {
    display: 'none'
  },
  color: '#656464'
}));

const FilterIcon = icons['filterList'];
const ExpandMoreIcon = icons['arrowDropDown'];

const isNotLOA = obj => {
  const compLevel = obj['Custom_Comp_Level'] ?? obj['Comp_LevelsX'];
  return compLevel !== 'LOA';
};

const AgentsCommissionsTableFilters = ({
  localLoader,
  filterCount,
  formData,
  clearFilters,
  handleChangeSelectValue,
  applyFilters,
  setformData,
  showFilters
}) => {
  const dispatch = useDispatch();
  const cancelTokenSource = axios.CancelToken.source();

  // Local states
  const [allLoaded, setAllLoaded] = useState(false);
  const initialLoad = useRef(true);

  // Date validation
  const isDateRangeValid = React.useMemo(() => {
    if (!formData.initial_date || !formData.end_date) return true;

    const initialDate = dayjs(formData.initial_date, 'MM-YYYY').startOf(
      'month'
    );
    const endDate = dayjs(formData.end_date, 'MM-YYYY').startOf('month');

    if (!initialDate.isValid() || !endDate.isValid()) return true;

    return (
      initialDate.isBefore(endDate) || initialDate.isSame(endDate, 'month')
    );
  }, [formData.initial_date, formData.end_date]);

  const { satementsCarriersArray } = useSelector(state => state.appointments);

  const { zohoData } = useSelector(state => state.user);

  const currentAgenciesAgentsDict = useSelector(
    state => state.agencyManagement.comissions_tab_agents
  );

  const { agencyInformation } = useSelector(
    state => state.agencyManagement.agencyData
  );

  const carriersOptions = React.useMemo(() => {
    return satementsCarriersArray
      .map(option => {
        return {
          type: option.type,
          name: option.name,
          id: option.AgencyBloc_CarrierID,
          Callidus_Carrier_Names: option.Callidus_Carrier_Names
        };
      })
      .concat([
        {
          type: 'Marketplace',
          name: 'Other',
          id: '',
          Callidus_Carrier_Names: 'Other'
        }
      ])
      .filter(carrier => carrier.type);
  }, [satementsCarriersArray]);

  const multiSelectOptions = React.useMemo(() => {
    if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return [];

    const filteredAgenciesOptions =
      agencyInformation.Downline_Agencies_Three?.concat([agencyInformation]);

    const returnAgenciesOptions =
      filteredAgenciesOptions?.map(agency => ({
        id: agency.id._value,
        name: agency.name._value
      })) || [];

    // Gets all the agents from all the agencies from the tree
    if (zohoData.Position_at_Agency === USER_TYPES.ADMIN) {
      const ids = [...returnAgenciesOptions?.map(agency => agency.id)];
      dispatch(
        getSelectedAgenciesAgents(
          () => {},
          currentAgenciesAgentsDict,
          ids,
          cancelTokenSource
        )
      );
    }

    return returnAgenciesOptions;
  }, [agencyInformation.Downline_Agencies_Three]);

  const agentOrAgencySelected = React.useMemo(
    () => formData.agents.length === 0 && formData.agencies.length === 0,
    [formData.agents, formData.agencies]
  );

  const agentsOptions = React.useMemo(() => {
    if (!zohoData.NPN || multiSelectOptions.length === 0) return [];

    const selectedAgenciesAgents = multiSelectOptions.flatMap(agency =>
      (currentAgenciesAgentsDict[agency.id] ?? [])
        .filter(isNotLOA)
        .map(agent => ({
          type: agent.Position_at_Agency,
          name: agent.First_Name + ' ' + agent.Last_Name,
          npn: agent.NPN,
          agentId: agent.id,
          agency: agency.name,
          agencyId: agency.id,
          Position_at_Agency: agent.Position_at_Agency,
          member_count: agent.member_count
        }))
    );

    return selectedAgenciesAgents;
  }, [currentAgenciesAgentsDict, multiSelectOptions]);

  // Effects
  // Gets data from backend
  useEffect(() => {
    const getData = async () => {
      dispatch(
        getStatementsPeriods(
          () => {},
          () => {}
        )
      );
      dispatch(
        getAppointmentsViewData(
          () => {},
          () => {}
        )
      );
      if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return;
      dispatch(
        getSpecificAgencyData(
          () => {},
          zohoData?.Account_Name?.id,
          () => {}
        )
      );
    };
    getData();
  }, []);

  // If all data from all sources has finished loading, turns off the local loader
  useEffect(() => {
    const carriersOptionsAreLoaded = carriersOptions.length > 1;
    const agenciesOptionsAreLoaded =
      multiSelectOptions.length > 0 ||
      zohoData.Position_at_Agency !== USER_TYPES.ADMIN;
    const agentsOptionsAreLoaded = agentsOptions.length > 0;

    if (
      carriersOptionsAreLoaded &&
      agentsOptionsAreLoaded &&
      agenciesOptionsAreLoaded &&
      !allLoaded
    ) {
      setAllLoaded(true);
    }
  }, [carriersOptions, agentsOptions, multiSelectOptions]);

  // Preselects main agent
  useEffect(() => {
    if (zohoData.NPN && allLoaded) {
      setformData(prevData => ({
        ...prevData,
        agents:
          zohoData.Position_at_Agency === 'Admin'
            ? agentsOptions
            : [
                {
                  type: 'Main Agent',
                  name: zohoData.First_Name + ' ' + zohoData.Last_Name,
                  npn: zohoData.NPN,
                  agentId: zohoData?.id,
                  agency: zohoData.agencyData.Account_Name,
                  agencyId: zohoData.Account_Name?.id,
                  Position_at_Agency: zohoData.Position_at_Agency,
                  member_count: zohoData.member_count
                }
              ],
        agencies:
          zohoData.Position_at_Agency === USER_TYPES.ADMIN
            ? multiSelectOptions
            : [
                {
                  id: zohoData.Account_Name?.id,
                  name: zohoData.Account_Name.name
                }
              ]
      }));
    }
  }, [agentsOptions, allLoaded, zohoData]);

  useEffect(() => {
    if (allLoaded && formData.agents.length > 0 && initialLoad.current) {
      applyFilters();
      initialLoad.current = false;
    }
  }, [formData.agents]);

  return (
    <div
      className='rounded-[14px] border border-[#d9d9da] flex flex-col w-full overflow-x-hidden overflow-y-auto'
      style={{ display: showFilters ? 'block' : 'none' }}
    >
      {/* Header */}
      <div
        data-tour='commissions-filters-header'
        className='flex bg-grey-lightest items-center p-[15px] gap-[15px] sticky top-0 z-10'
      >
        <FilterIcon />
        <h4>Filters ({filterCount})</h4>
      </div>

      {/* Body */}
      <div className='bg-white flex-1 flex flex-col items-center overflow-y-auto'>
        <div className='border-b border-[#f6f6f6] w-[85%]' />

        <div className='flex flex-col gap-6 w-[85%] my-5'>
          {/* Agencies — admin only */}
          {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
            <AutocompleteInput
              showScrollbar
              multiple
              loading={localLoader}
              loadingBlocksOptions={true}
              options={multiSelectOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              getOptionLabel={option => option.name}
              id='agencies'
              value={formData.agencies}
              onValueChanges={e => {
                handleChangeSelectValue(e, 'agencies');
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={'Payee Agency(s)'}
                  placeholder={'Select agencies'}
                  error={agentOrAgencySelected}
                  helperText={
                    agentOrAgencySelected
                      ? 'You need to select at least 1 agency or 1 agent'
                      : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {localLoader ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                />
              )}
            />
          )}

          {/* Agents — admin only */}
          {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
            <AutocompleteInput
              label={'agents'}
              multiple
              selectAll
              loading={!allLoaded}
              disabled={!allLoaded}
              options={!allLoaded ? [] : agentsOptions}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              groupBy={option => option.agency}
              getOptionLabel={option => option.name}
              id='downlineagents'
              value={!allLoaded ? [] : formData.agents}
              onValueChanges={e => handleChangeSelectValue(e, 'agents')}
              renderInput={params => (
                <TextField
                  {...params}
                  label={'Payee Agent(s)'}
                  placeholder={'Select agents'}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {!allLoaded ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                />
              )}
              selectAllByGroup={true}
            />
          )}

          {/* Carrier */}
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='carrier-header'
              id='carrier-header'
            >
              Carrier
            </AccordionSummary>
            <AccordionDetails style={{ borderLeft: '5px solid #d0d0d1' }}>
              <AutocompleteInput
                multiple
                loading={localLoader}
                options={carriersOptions
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .sort((a, b) => -b.type.localeCompare(a.type))}
                isOptionEqualToValue={(option, value) =>
                  option.name === value.name
                }
                groupBy={option => option.type}
                getOptionLabel={option => option.name}
                id='carriers'
                value={formData.carriers}
                onValueChanges={e => handleChangeSelectValue(e, 'carriers')}
                disabled={formData.includeAllCarriers}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Carrier(s)'
                    placeholder={'Select carriers'}
                    InputLabelProps={{
                      shrink: !formData.includeAllCarriers
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {localLoader ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      )
                    }}
                  />
                )}
              />
            </AccordionDetails>
          </Accordion>

          {/* State */}
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='pstate-header'
              id='state-header'
            >
              State
            </AccordionSummary>
            <AccordionDetails style={{ borderLeft: '5px solid #d0d0d1' }}>
              <SimpleFilter
                options={STATE_LIST}
                value={formData.states}
                handleChange={event => {
                  const {
                    target: { value }
                  } = event;
                  handleChangeSelectValue(value, 'states');
                }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Initial Date */}
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='dates-header'
              id='dates-header'
            >
              Initial Date
            </AccordionSummary>
            <AccordionDetails
              style={{ borderLeft: '5px solid #d0d0d1', paddingBottom: '20px' }}
            >
              <div className='flex flex-col items-center w-full mt-2'>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Initial Date'
                    value={
                      formData.initial_date
                        ? dayjs(formData.initial_date, 'MM-YYYY')
                        : dayjs().subtract(4, 'month')
                    }
                    onChange={date => {
                      const formattedDate = date ? date.format('MM-YYYY') : '';
                      handleChangeSelectValue(formattedDate, 'initial_date');
                    }}
                    views={['year', 'month']}
                    openTo='month'
                    minDate={dayjs().subtract(10, 'year')}
                    maxDate={dayjs().subtract(1, 'month')}
                    renderInput={params => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* End Date */}
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='dates-header'
              id='dates-header-end'
            >
              End Date
            </AccordionSummary>
            <AccordionDetails
              style={{ borderLeft: '5px solid #d0d0d1', paddingBottom: '20px' }}
            >
              <div className='flex flex-col items-center w-full mt-2'>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='End Date'
                    value={
                      formData.end_date
                        ? dayjs(formData.end_date, 'MM-YYYY')
                        : dayjs().subtract(1, 'month')
                    }
                    onChange={date => {
                      const formattedDate = date ? date.format('MM-YYYY') : '';
                      handleChangeSelectValue(formattedDate, 'end_date');
                    }}
                    views={['year', 'month']}
                    openTo='month'
                    minDate={dayjs().subtract(10, 'year')}
                    maxDate={dayjs()}
                    renderInput={params => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
                {!isDateRangeValid && (
                  <FormHelperText
                    error
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  >
                    The end date must be after or the same as the start date.
                  </FormHelperText>
                )}
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Agent Status */}
          <Accordion slotProps={{ heading: { component: 'h4' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='agent-status-header'
              id='agent-status-header'
            >
              <div className='flex items-center gap-1'>
                <span>Agent Status</span>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <SimpleFilter
                options={['Active', 'Inactive']}
                value={formData.agentStatus}
                handleChange={event => {
                  const {
                    target: { value }
                  } = event;
                  handleChangeSelectValue(value, 'agentStatus');
                }}
              />
            </AccordionDetails>
          </Accordion>
        </div>

        {/* Footer: Clear / Apply */}
        <div className='flex flex-col gap-3 justify-center py-4 sticky top-0 bg-white z-2 w-full'>
          {Array.isArray(formData.agentStatus) &&
            formData.agentStatus.length === 0 && (
              <FormHelperText
                error
                sx={{ mb: 0.5, fontSize: '0.75rem', width: '100%' }}
              >
                An Agent status must be selected
              </FormHelperText>
            )}
          <div className='flex flex-row gap-3 justify-center'>
            <Button
              text='Clear All'
              size='medium'
              variant='secondary'
              onClick={() => clearFilters()}
            />
            <Button
              text='Apply Filters'
              size='medium'
              variant='primaryDarkened'
              onClick={() => applyFilters()}
              isDisabled={
                !isDateRangeValid ||
                (Array.isArray(formData.agentStatus) &&
                  formData.agentStatus.length === 0)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

AgentsCommissionsTableFilters.propTypes = {
  localLoader: PropTypes.bool,
  filterCount: PropTypes.number,
  formData: PropTypes.object,
  clearFilters: PropTypes.func,
  handleChangeSelectValue: PropTypes.func,
  applyFilters: PropTypes.func,
  setformData: PropTypes.func,
  showFilters: PropTypes.bool
};

export default memo(AgentsCommissionsTableFilters);
